<template>
  <div class="element-label__show-footnotes-wrapper">
    <div>{{ t('author.showReferences') }}</div>
    <BbSwitch v-model="footnoteValue" :items="footnoteValues" class="element-label__switch" />
  </div>
</template>
<script>
import { computed, ref, watch } from 'vue';
import { useI18n } from 'vue-i18n';
import { useElementHighlight, useWesley } from '@anthill-modules/wesley-components';

import { mapReferencesFromBullets, removeFootnotesFromLayout, renderFootnotesInLayout } from '~/utils/footnotes';
import useDocumentEditor from '~/composables/useDocumentEditor';
import useAuthoring from '~/composables/useAuthoring';
import useSecondOrderReferencesGating from '~/composables/useSecondOrderReferencesGating';

export default {
  setup() {
    const { sendMessage: sendMessageToDocument, asyncSendMessage: sendAsyncMessageToDocument } = useWesley();
    const { selectedParent: selectedLayout } = useElementHighlight();
    const { updateEditorSelectableElementsData } = useDocumentEditor();
    const { footnotes } = useAuthoring();
    const { isSecondOrderReferencesOrg } = useSecondOrderReferencesGating();
    const { t } = useI18n();

    const footnoteValue = ref(
      selectedLayout.value && selectedLayout.value.attributes?.['show-footnotes'] !== 'true' ? 0 : 1,
    );
    const footnoteValues = [
      {
        title: t('common.no'),
        value: 0,
      },
      {
        title: t('common.yes'),
        value: 1,
      },
    ];
    const showFootnotes = computed(() => {
      return selectedLayout.value?.attributes?.['show-footnotes'] === 'true';
    });

    const mapReferencesFromBulletsCurrentLayout = () => {
      const htmlString = selectedLayout.value.element.html;
      return mapReferencesFromBullets(htmlString, footnotes.value, isSecondOrderReferencesOrg.value);
    };

    const addFootnotesElement = () => {
      const referencesCurrentLayout = mapReferencesFromBulletsCurrentLayout();
      renderFootnotesInLayout({
        t,
        layout: selectedLayout.value,
        footnotes: referencesCurrentLayout,
        sendMessageToDocument,
        sendAsyncMessageToDocument,
      });
    };

    const removeFootnotesElement = () => {
      removeFootnotesFromLayout({ layout: selectedLayout.value, sendMessageToDocument });
    };

    watch(showFootnotes, (newValue) => {
      if (newValue) {
        addFootnotesElement();
      } else {
        removeFootnotesElement();
      }
      updateEditorSelectableElementsData();
    });

    watch(footnoteValue, () => {
      const layoutPath = selectedLayout.value?.cssPath;

      if (layoutPath) {
        sendMessageToDocument({
          method: 'updateAttribute',
          params: {
            query: layoutPath,
            attr: 'show-footnotes',
            value: footnoteValue.value === 1,
          },
        });
        selectedLayout.value.attributes['show-footnotes'] = footnoteValue.value === 1 ? 'true' : 'false';
        updateEditorSelectableElementsData();
      }
    });

    return {
      footnoteValue,
      footnoteValues,
      t,
    };
  },
};
</script>

<style lang="scss" scoped>
.element-label {
  &__show-footnotes-wrapper {
    padding: $bb-spacing-1 $bb-spacing-2;
    background: rgba($bb-primary-1, 0.85);
    border-radius: $bb-spacing-1;
    display: flex;
    gap: $bb-spacing-2;
    align-items: center;
    font-weight: 500;
    z-index: 2;
    font-size: 12px;
    color: $bb-neutral-0;
  }

  &__switch {
    pointer-events: inherit;
    width: 100px;
    padding: 2px 0;
    background: transparent;

    :deep(.switch-item) {
      height: $bb-spacing-4 !important;
      padding: 0 !important;
    }

    :deep(.switch-item .v-slide-group__container .v-slide-group__content) {
      button {
        height: 14px;
      }
    }

    :deep(.switch-item .v-slide-group__container .v-slide-group__content .v-tab--selected) {
      background-color: $bb-primary-3 !important;
    }
  }
}
</style>
